import api from "@/lib/api";

// Downscale phone photos (5–12MB) to ~1800px JPEG before upload — typically
// an 80-90% payload cut, which is the difference between 30s and 3s on site.
export async function compressImage(file, maxDim = 1800, quality = 0.82) {
  if (!file?.type?.startsWith("image/") || file.type === "image/gif") return file;
  if (file.size < 400 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file; // HEIC or decode failure — upload original rather than block
  }
}

export async function uploadPhoto(file, category, onProgress, { onlineOnly = false } = {}) {
  const small = await compressImage(file);
  const fd = new FormData();
  fd.append("file", small, small.name || "photo.jpg");
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, {
    __flOnlineOnly: onlineOnly,
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (ev) => {
      if (ev.total) onProgress?.(Math.min(99, Math.round((ev.loaded / ev.total) * 100)));
    },
  });
  return { path: data.storage_path || data.path, offline: !!data.offline };
}

// Upload a batch in parallel (3 at a time) with per-item callbacks.
// items: [{ key, file }]
export async function uploadMany(items, category, { onProgress, onDone, onError } = {}, concurrency = 3) {
  let i = 0;
  const worker = async () => {
    while (i < items.length) {
      const item = items[i++];
      try {
        const res = await uploadPhoto(item.file, category, (pct) => onProgress?.(item.key, pct));
        onDone?.(item.key, res);
      } catch (err) {
        onError?.(item.key, err);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
}
